import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiShield,
  FiFileText,
  FiDatabase,
  FiLayers,
  FiSettings,
  FiClock,
  FiGlobe,
  FiUserCheck,
  FiShare2,
  FiLock,
  FiUsers,
  FiExternalLink,
  FiRefreshCw,
  FiMail,
  FiCalendar,
  FiCheckCircle,
  FiUser,
  FiActivity,
  FiBriefcase,
  FiAlertTriangle,
  FiChevronRight,
  FiArrowUp,
  FiList
} from 'react-icons/fi';
import './PrivacyPolicy.css';

const TOC_ITEMS = [
  { id: 'interpretation-definitions', label: '1. Interpretation & Definitions', icon: FiFileText },
  { id: 'collecting-using-data', label: '2. Collecting & Using Data', icon: FiDatabase },
  { id: 'tracking-technologies-cookies', label: '3. Tracking & Cookies', icon: FiLayers },
  { id: 'use-of-data', label: '4. Use of Your Personal Data', icon: FiSettings },
  { id: 'retention-of-data', label: '5. Retention of Data', icon: FiClock },
  { id: 'transfer-of-data', label: '6. Transfer of Data', icon: FiGlobe },
  { id: 'delete-your-data', label: '7. Delete Your Personal Data', icon: FiUserCheck },
  { id: 'disclosure-of-data', label: '8. Disclosure of Data', icon: FiShare2 },
  { id: 'security-of-data', label: '9. Security of Personal Data', icon: FiLock },
  { id: 'childrens-privacy', label: "10. Children's Privacy", icon: FiUsers },
  { id: 'links-to-other-websites', label: '11. Links to Other Websites', icon: FiExternalLink },
  { id: 'changes-to-policy', label: '12. Changes to this Policy', icon: FiRefreshCw },
  { id: 'contact-us', label: '13. Contact Us', icon: FiMail },
  { id: 'policy-notes', label: '14. Note & Related Policies', icon: FiShield },
];

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('interpretation-definitions');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }

      // Determine active section based on scroll position
      const scrollPosition = window.scrollY + 180;
      for (let i = TOC_ITEMS.length - 1; i >= 0; i--) {
        const section = document.getElementById(TOC_ITEMS[i].id);
        if (section) {
          const top = section.offsetTop;
          if (scrollPosition >= top) {
            setActiveSection(TOC_ITEMS[i].id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setActiveSection(id);
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -90; // Offset for navbar
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="privacy-page-wrapper">
      {/* Background Decorative Mesh Orbs */}
      <div className="privacy-bg-ambient" aria-hidden="true">
        <div className="privacy-ambient-orb orb-primary"></div>
        <div className="privacy-ambient-orb orb-secondary"></div>
        <div className="privacy-ambient-orb orb-tertiary"></div>
      </div>

      {/* Hero Header Section */}
      <section className="privacy-hero-section">
        <div className="privacy-hero-container">
          <div className="privacy-hero-content">
            {/* Pill Badge */}
            <div className="privacy-hero-badge">
              <FiShield className="privacy-badge-icon" />
              <span>Legal</span>
            </div>

            {/* Gradient Title */}
            <h1 className="privacy-hero-title">Privacy Policy</h1>

            {/* Subtitle / Intro Description */}
            <p className="privacy-hero-subtitle">
              Your privacy is important to me. This page explains how I collect, use, and protect
              your information when you visit my portfolio website.
            </p>

            {/* Metadata Pill */}
            <div className="privacy-hero-meta">
              <div className="privacy-meta-item">
                <FiCalendar className="privacy-meta-icon" />
                <span>
                  <strong>Last Updated On: </strong>28-03-2026
                </span>
              </div>
            </div>
          </div>

          {/* Hero Right Visual: High-tech 3D Cyber Security Shield Graphic */}
          <div className="privacy-hero-visual" aria-hidden="true">
            <div className="privacy-shield-canvas">
              {/* Central Glowing Shield */}
              <div className="shield-glow-backdrop"></div>
              
              <svg className="privacy-shield-svg" viewBox="0 0 280 280" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Orbit track ring */}
                <circle cx="140" cy="140" r="115" stroke="currentColor" strokeWidth="1.5" strokeDasharray="6 6" className="shield-orbit-ring" />
                <circle cx="140" cy="140" r="85" stroke="currentColor" strokeWidth="1" strokeOpacity="0.25" />
                
                {/* Connector rays */}
                <line x1="140" y1="140" x2="225" y2="70" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" strokeDasharray="3 3" />
                <line x1="140" y1="140" x2="250" y2="155" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" strokeDasharray="3 3" />
                <line x1="140" y1="140" x2="65" y2="205" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" strokeDasharray="3 3" />
                
                {/* Shield Body */}
                <defs>
                  <linearGradient id="shieldGrad" x1="140" y1="75" x2="140" y2="205" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#38bdf8" />
                    <stop offset="50%" stopColor="#2563eb" />
                    <stop offset="100%" stopColor="#1e3a8a" />
                  </linearGradient>
                  <linearGradient id="shieldGlow" x1="90" y1="75" x2="190" y2="205" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#9333ea" stopOpacity="0.7" />
                  </linearGradient>
                  <filter id="shieldShadow" x="70" y="60" width="140" height="160" filterUnits="userSpaceOnUse">
                    <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor="#2563eb" floodOpacity="0.4" />
                  </filter>
                </defs>
                
                {/* Main Shield Path */}
                <path
                  d="M140 75 L186 92 C186 142 166 182 140 205 C114 182 94 142 94 92 L140 75 Z"
                  fill="url(#shieldGrad)"
                  filter="url(#shieldShadow)"
                  stroke="url(#shieldGlow)"
                  strokeWidth="3"
                />
                
                {/* Inner Shield Accent */}
                <path
                  d="M140 85 L176 99 C176 139 159 171 140 191 C121 171 104 139 104 99 L140 85 Z"
                  fill="none"
                  stroke="#ffffff"
                  strokeOpacity="0.25"
                  strokeWidth="1.5"
                />

                {/* Padlock Icon in Center */}
                {/* Lock shackle */}
                <path
                  d="M132 135 V125 C132 120.6 135.6 117 140 117 C144.4 117 148 120.6 148 125 V135"
                  stroke="#ffffff"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                />
                {/* Lock body */}
                <rect x="127" y="134" width="26" height="22" rx="4" fill="#ffffff" />
                <circle cx="140" cy="143" r="2.5" fill="#1e3a8a" />
                <path d="M140 145.5 V150" stroke="#1e3a8a" strokeWidth="2" strokeLinecap="round" />
              </svg>

              {/* Floating Node 1: Purple User Circle */}
              <div className="floating-bubble bubble-purple" style={{ top: '18%', right: '14%' }}>
                <FiUser className="bubble-icon" />
              </div>

              {/* Floating Node 2: Blue Globe Circle */}
              <div className="floating-bubble bubble-blue" style={{ top: '50%', right: '0%' }}>
                <FiGlobe className="bubble-icon" />
              </div>

              {/* Floating Node 3: Teal Document Circle */}
              <div className="floating-bubble bubble-teal" style={{ bottom: '15%', left: '12%' }}>
                <FiFileText className="bubble-icon" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Layout with Sidebar & Content Cards */}
      <div className="privacy-body-container">
        {/* Mobile Table of Contents Accordion Toggle */}
        <div className="privacy-mobile-toc-toggle">
          <button
            className="mobile-toc-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
          >
            <FiList className="toc-toggle-icon" />
            <span>On This Page: {TOC_ITEMS.find((item) => item.id === activeSection)?.label || 'Sections'}</span>
            <FiChevronRight className={`toc-toggle-arrow ${mobileMenuOpen ? 'open' : ''}`} />
          </button>
        </div>

        {/* Left Column: Sticky Table of Contents */}
        <aside className={`privacy-sidebar-nav ${mobileMenuOpen ? 'mobile-visible' : ''}`}>
          <div className="privacy-toc-card">
            <h2 className="privacy-toc-title">On This Page</h2>
            <nav className="privacy-toc-list" aria-label="Table of Contents">
              {TOC_ITEMS.map((item) => {
                const IconComponent = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`privacy-toc-item ${isActive ? 'active' : ''}`}
                    type="button"
                  >
                    <span className="toc-item-indicator" aria-hidden="true" />
                    <IconComponent className="toc-item-icon" />
                    <span className="toc-item-text">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Right Column: Main Content Cards */}
        <main className="privacy-content-stream">
          {/* Section 1: Interpretation and Definitions */}
          <section id="interpretation-definitions" className="privacy-card">
            <div className="privacy-card-header">
              <div className="privacy-section-icon-box box-blue">
                <FiFileText className="privacy-section-icon" />
              </div>
              <h2 className="privacy-card-title">
                1. Interpretation and <span className="title-gradient">Definitions</span>
              </h2>
            </div>

            <div className="privacy-card-body">
              <h3 className="privacy-subheading">Interpretation</h3>
              <p className="privacy-paragraph">
                The words of which the initial letter is capitalized have meanings defined under the
                following conditions. The following definitions shall have the same meaning regardless
                of whether they appear in singular or in plural.
              </p>

              <h3 className="privacy-subheading">Definitions</h3>
              <p className="privacy-paragraph">For the purposes of this Privacy Policy:</p>

              <div className="privacy-definitions-grid">
                <div className="definition-item">
                  <div className="def-term">Account</div>
                  <div className="def-desc">means a unique account created for You to access our Service or parts of our Service.</div>
                </div>

                <div className="definition-item">
                  <div className="def-term">Affiliate</div>
                  <div className="def-desc">means an entity that controls, is controlled by or is under common control with a party...</div>
                </div>

                <div className="definition-item">
                  <div className="def-term">Company</div>
                  <div className="def-desc">refers to Praveen Portfolio.</div>
                </div>

                <div className="definition-item">
                  <div className="def-term">Cookies</div>
                  <div className="def-desc">are small files that are placed on Your computer...</div>
                </div>

                <div className="definition-item">
                  <div className="def-term">Country</div>
                  <div className="def-desc">refers to: Tamil Nadu, India</div>
                </div>

                <div className="definition-item">
                  <div className="def-term">Device</div>
                  <div className="def-desc">means any device that can access the Service...</div>
                </div>

                <div className="definition-item">
                  <div className="def-term">Personal Data</div>
                  <div className="def-desc">is any information that relates to an identified or identifiable individual.</div>
                </div>

                <div className="definition-item">
                  <div className="def-term">Service</div>
                  <div className="def-desc">refers to the Website.</div>
                </div>

                <div className="definition-item">
                  <div className="def-term">Service Provider</div>
                  <div className="def-desc">means any natural or legal person who processes the data on behalf of the Company...</div>
                </div>

                <div className="definition-item">
                  <div className="def-term">Usage Data</div>
                  <div className="def-desc">refers to data collected automatically...</div>
                </div>

                <div className="definition-item">
                  <div className="def-term">Website</div>
                  <div className="def-desc">
                    refers to Praveen Kumar Portfolio, accessible from{' '}
                    <a
                      href="https://praveen-kumar-b.web.app/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="privacy-inline-link"
                    >
                      Click to see Website
                    </a>
                  </div>
                </div>

                <div className="definition-item">
                  <div className="def-term">You</div>
                  <div className="def-desc">means the individual accessing or using the Service...</div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Collecting and Using Your Personal Data */}
          <section id="collecting-using-data" className="privacy-card">
            <div className="privacy-card-header">
              <div className="privacy-section-icon-box box-purple">
                <FiDatabase className="privacy-section-icon" />
              </div>
              <h2 className="privacy-card-title">
                2. Collecting and Using Your <span className="title-gradient">Personal Data</span>
              </h2>
            </div>

            <div className="privacy-card-body">
              <h3 className="privacy-subheading">Types of Data Collected</h3>
              
              {/* Highlight Cards Matching Reference Design */}
              <div className="privacy-highlight-grid">
                {/* Personal Information Card */}
                <div className="highlight-card card-personal">
                  <div className="highlight-card-header">
                    <div className="highlight-badge badge-blue">
                      <FiUser />
                    </div>
                    <div className="highlight-title-group">
                      <h4 className="highlight-title">Personal Data</h4>
                      <span className="highlight-tag">Provided by You</span>
                    </div>
                  </div>
                  <p className="highlight-text">
                    While using Our Service, We may ask You to provide Us with certain personally identifiable information...
                  </p>
                  <ul className="highlight-list">
                    <li>Email address</li>
                    <li>First name and last name</li>
                    <li>Phone number</li>
                    <li>Address, State, Province, ZIP/Postal code, City</li>
                    <li>Usage Data</li>
                  </ul>
                </div>

                {/* Usage Information Card */}
                <div className="highlight-card card-usage">
                  <div className="highlight-card-header">
                    <div className="highlight-badge badge-green">
                      <FiActivity />
                    </div>
                    <div className="highlight-title-group">
                      <h4 className="highlight-title">Usage Data</h4>
                      <span className="highlight-tag">Collected Automatically</span>
                    </div>
                  </div>
                  <p className="highlight-text">
                    Usage Data is collected automatically when using the Service. Pages visited, time spent, browser type, device type, and diagnostic analytics data.
                  </p>
                </div>

                {/* Cookies Card */}
                <div className="highlight-card card-cookies">
                  <div className="highlight-card-header">
                    <div className="highlight-badge badge-amber">
                      <FiLayers />
                    </div>
                    <div className="highlight-title-group">
                      <h4 className="highlight-title">Cookies & Tracking</h4>
                      <span className="highlight-tag">Session & Persistent</span>
                    </div>
                  </div>
                  <p className="highlight-text">
                    Information collected through cookies and similar technologies. (See{' '}
                    <a
                      href="https://policiespraveenkumarb.netlify.app/cookie-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="privacy-inline-link"
                    >
                      Cookies Policy
                    </a>
                    )
                  </p>
                </div>
              </div>

              <h3 className="privacy-subheading" style={{ marginTop: '28px' }}>Usage Data</h3>
              <p className="privacy-paragraph">
                Usage Data is collected automatically when using the Service.
              </p>
              <p className="privacy-paragraph">
                Usage Data may include information such as Your Device's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that You visit, the time and date of Your visit, the time spent on those pages, unique device identifiers and other diagnostic data.
              </p>
              <p className="privacy-paragraph">
                When You access the Service by or through a mobile device, We may collect certain information automatically, including, but not limited to, the type of mobile device You use, Your mobile device unique ID, the IP address of Your mobile device, Your mobile operating system, the type of mobile Internet browser You use, unique device identifiers and other diagnostic data.
              </p>
              <p className="privacy-paragraph">
                We may also collect information that Your browser sends whenever You visit our Service or when You access the Service by or through a mobile device.
              </p>
            </div>
          </section>

          {/* Section 3: Tracking Technologies and Cookies */}
          <section id="tracking-technologies-cookies" className="privacy-card">
            <div className="privacy-card-header">
              <div className="privacy-section-icon-box box-amber">
                <FiLayers className="privacy-section-icon" />
              </div>
              <h2 className="privacy-card-title">
                3. Tracking Technologies and <span className="title-gradient">Cookies</span>
              </h2>
            </div>

            <div className="privacy-card-body">
              <p className="privacy-paragraph">
                We use Cookies and similar tracking technologies to track the activity on Our Service and store certain information. Tracking technologies used are beacons, tags, and scripts to collect and track information and to improve and analyze Our Service. The technologies We use may include:
              </p>

              <div className="privacy-tech-cards">
                <div className="tech-card">
                  <h4 className="tech-card-title">
                    <span className="tech-dot dot-blue"></span> Cookies or Browser Cookies
                  </h4>
                  <p className="privacy-paragraph">
                    A cookie is a small file placed on Your Device. You can instruct Your browser to refuse all Cookies or to indicate when a Cookie is being sent. However, if You do not accept Cookies, You may not be able to use some parts of our Service. Unless you have adjusted Your browser setting so that it will refuse Cookies, our Service may use Cookies.
                  </p>
                </div>

                <div className="tech-card">
                  <h4 className="tech-card-title">
                    <span className="tech-dot dot-purple"></span> Web Beacons
                  </h4>
                  <p className="privacy-paragraph">
                    Certain sections of our Service and our emails may contain small electronic files known as web beacons (also referred to as clear gifs, pixel tags, and single-pixel gifs) that permit the Company, for example, to count users who have visited those pages or opened an email and for other related website statistics (for example, recording the popularity of a certain section and verifying system and server integrity).
                  </p>
                  <p className="privacy-paragraph">
                    Cookies can be "Persistent" or "Session" Cookies. Persistent Cookies remain on Your personal computer or mobile device when You go offline, while Session Cookies are deleted as soon as You close Your web browser.
                  </p>
                </div>
              </div>

              <div className="cookie-types-container">
                <h4 className="privacy-subheading-small">Cookie Classifications We Use</h4>
                <div className="cookie-types-grid">
                  <div className="cookie-type-badge">
                    <div className="cookie-type-name">Necessary / Essential Cookies</div>
                    <div className="cookie-type-val val-session">Session Cookies</div>
                  </div>
                  <div className="cookie-type-badge">
                    <div className="cookie-type-name">Cookies Policy / Notice Acceptance Cookies</div>
                    <div className="cookie-type-val val-persistent">Persistent Cookies</div>
                  </div>
                  <div className="cookie-type-badge">
                    <div className="cookie-type-name">Functionality Cookies</div>
                    <div className="cookie-type-val val-persistent">Persistent Cookies</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Use of Your Personal Data */}
          <section id="use-of-data" className="privacy-card">
            <div className="privacy-card-header">
              <div className="privacy-section-icon-box box-indigo">
                <FiSettings className="privacy-section-icon" />
              </div>
              <h2 className="privacy-card-title">
                4. Use of Your <span className="title-gradient">Personal Data</span>
              </h2>
            </div>

            <div className="privacy-card-body">
              <p className="privacy-paragraph">
                The Company may use Personal Data for the following purposes:
              </p>

              {/* Styled Circular Checkmark List Matching Reference Image */}
              <ul className="privacy-checkmark-list">
                <li className="checkmark-item">
                  <FiCheckCircle className="checkmark-icon" />
                  <div className="checkmark-content">
                    <strong>To provide and maintain our Service:</strong> including to monitor the usage of our Service.
                  </div>
                </li>
                <li className="checkmark-item">
                  <FiCheckCircle className="checkmark-icon" />
                  <div className="checkmark-content">
                    <strong>To manage Your Account:</strong> to manage Your registration as a user of the Service. The Personal Data You provide can give You access to different functionalities of the Service that are available to You as a registered user.
                  </div>
                </li>
                <li className="checkmark-item">
                  <FiCheckCircle className="checkmark-icon" />
                  <div className="checkmark-content">
                    <strong>For the performance of a contract:</strong> the development, compliance and undertaking of the purchase contract for the products, items or services You have purchased or of any other contract with Us through the Service.
                  </div>
                </li>
                <li className="checkmark-item">
                  <FiCheckCircle className="checkmark-icon" />
                  <div className="checkmark-content">
                    <strong>To contact You:</strong> To contact You by email, telephone calls, SMS, or other equivalent forms of electronic communication, such as a mobile application's push notifications regarding updates or informative communications related to the functionalities, products or contracted services, including the security updates, when necessary or reasonable for their implementation.
                  </div>
                </li>
                <li className="checkmark-item">
                  <FiCheckCircle className="checkmark-icon" />
                  <div className="checkmark-content">
                    <strong>To provide You with news and offers:</strong> with news, special offers and general information about other goods, services and events which we offer that are similar to those that you have already purchased or enquired about unless You have opted not to receive such information.
                  </div>
                </li>
                <li className="checkmark-item">
                  <FiCheckCircle className="checkmark-icon" />
                  <div className="checkmark-content">
                    <strong>To manage Your requests:</strong> To attend and manage Your requests to Us.
                  </div>
                </li>
                <li className="checkmark-item">
                  <FiCheckCircle className="checkmark-icon" />
                  <div className="checkmark-content">
                    <strong>For business transfers:</strong> We may use Your information to evaluate or conduct a merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or all of Our assets, whether as a going concern or as part of bankruptcy, liquidation, or similar proceeding, in which Personal Data held by Us about our Service users is among the assets transferred.
                  </div>
                </li>
                <li className="checkmark-item">
                  <FiCheckCircle className="checkmark-icon" />
                  <div className="checkmark-content">
                    <strong>For other purposes:</strong> We may use Your information for other purposes, such as data analysis, identifying usage trends, determining the effectiveness of our promotional campaigns and to evaluate and improve our Service, products, services, marketing and your experience.
                  </div>
                </li>
              </ul>

              <h3 className="privacy-subheading" style={{ marginTop: '32px' }}>
                We may share Your personal information in the following situations:
              </h3>

              <div className="privacy-sharing-grid">
                <div className="sharing-card">
                  <div className="sharing-card-title">With Service Providers:</div>
                  <div className="sharing-card-desc">
                    We may share Your personal information with Service Providers to monitor and analyze the use of our Service, to contact You.
                  </div>
                </div>

                <div className="sharing-card">
                  <div className="sharing-card-title">For business transfers:</div>
                  <div className="sharing-card-desc">
                    We may share or transfer Your personal information in connection with, or during negotiations of, any merger, sale of Company assets, financing, or acquisition of all or a portion of Our business to another company.
                  </div>
                </div>

                <div className="sharing-card">
                  <div className="sharing-card-title">With Affiliates:</div>
                  <div className="sharing-card-desc">
                    We may share Your information with Our affiliates, in which case we will require those affiliates to honor this Privacy Policy. Affiliates include Our parent company and any other subsidiaries, joint venture partners or other companies that We control or that are under common control with Us.
                  </div>
                </div>

                <div className="sharing-card">
                  <div className="sharing-card-title">With business partners:</div>
                  <div className="sharing-card-desc">
                    We may share Your information with Our business partners to offer You certain products, services or promotions.
                  </div>
                </div>

                <div className="sharing-card">
                  <div className="sharing-card-title">With other users:</div>
                  <div className="sharing-card-desc">
                    when You share personal information or otherwise interact in the public areas with other users, such information may be viewed by all users and may be publicly distributed outside.
                  </div>
                </div>

                <div className="sharing-card">
                  <div className="sharing-card-title">With Your consent:</div>
                  <div className="sharing-card-desc">
                    We may disclose Your personal information for any other purpose with Your consent.
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5: Retention of Your Personal Data */}
          <section id="retention-of-data" className="privacy-card">
            <div className="privacy-card-header">
              <div className="privacy-section-icon-box box-teal">
                <FiClock className="privacy-section-icon" />
              </div>
              <h2 className="privacy-card-title">
                5. Retention of Your <span className="title-gradient">Personal Data</span>
              </h2>
            </div>

            <div className="privacy-card-body">
              <p className="privacy-paragraph">
                The Company will retain Your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use Your Personal Data to the extent necessary to comply with our legal obligations (for example, if we are required to retain your data to comply with applicable laws), resolve disputes, and enforce our legal agreements and policies.
              </p>
              <p className="privacy-paragraph">
                The Company will also retain Usage Data for internal analysis purposes. Usage Data is generally retained for a shorter period of time, except when this data is used to strengthen the security or to improve the functionality of Our Service, or We are legally obligated to retain this data for longer time periods.
              </p>
            </div>
          </section>

          {/* Section 6: Transfer of Your Personal Data */}
          <section id="transfer-of-data" className="privacy-card">
            <div className="privacy-card-header">
              <div className="privacy-section-icon-box box-cyan">
                <FiGlobe className="privacy-section-icon" />
              </div>
              <h2 className="privacy-card-title">
                6. Transfer of Your <span className="title-gradient">Personal Data</span>
              </h2>
            </div>

            <div className="privacy-card-body">
              <p className="privacy-paragraph">
                Your information, including Personal Data, is processed at the Company's operating offices and in any other places where the parties involved in the processing are located. It means that this information may be transferred to — and maintained on — computers located outside of Your state, province, country or other governmental jurisdiction where the data protection laws may differ than those from Your jurisdiction.
              </p>
              <p className="privacy-paragraph">
                Your consent to this Privacy Policy followed by Your submission of such information represents Your agreement to that transfer.
              </p>
              <p className="privacy-paragraph">
                The Company will take all steps reasonably necessary to ensure that Your data is treated securely and in accordance with this Privacy Policy and no transfer of Your Personal Data will take place to an organization or a country unless there are adequate controls in place including the security of Your data and other personal information.
              </p>
            </div>
          </section>

          {/* Section 7: Delete Your Personal Data */}
          <section id="delete-your-data" className="privacy-card">
            <div className="privacy-card-header">
              <div className="privacy-section-icon-box box-rose">
                <FiUserCheck className="privacy-section-icon" />
              </div>
              <h2 className="privacy-card-title">
                7. Delete Your <span className="title-gradient">Personal Data</span>
              </h2>
            </div>

            <div className="privacy-card-body">
              <p className="privacy-paragraph">
                You have the right to delete or request that We assist in deleting the Personal Data that We have collected about You.
              </p>
              <p className="privacy-paragraph">
                Our Service may give You the ability to delete certain information about You from within the Service.
              </p>
              <p className="privacy-paragraph">
                You may update, amend, or delete Your information at any time by signing in to Your Account, if you have one, and visiting the account settings section that allows you to manage Your personal information. You may also contact Us to request access to, correct, or delete any personal information that You have provided to Us.
              </p>
              <p className="privacy-paragraph">
                Please note, however, that We may need to retain certain information when we have a legal obligation or lawful basis to do so.
              </p>
            </div>
          </section>

          {/* Section 8: Disclosure of Your Personal Data */}
          <section id="disclosure-of-data" className="privacy-card">
            <div className="privacy-card-header">
              <div className="privacy-section-icon-box box-purple">
                <FiShare2 className="privacy-section-icon" />
              </div>
              <h2 className="privacy-card-title">
                8. Disclosure of Your <span className="title-gradient">Personal Data</span>
              </h2>
            </div>

            <div className="privacy-card-body">
              <div className="disclosure-subblock">
                <h3 className="disclosure-heading">
                  <FiBriefcase className="disclosure-icon" /> Business Transactions:
                </h3>
                <p className="privacy-paragraph">
                  If the Company is involved in a merger, acquisition or asset sale, Your Personal Data may be transferred. We will provide notice before Your Personal Data is transferred and becomes subject to a different Privacy Policy.
                </p>
              </div>

              <div className="disclosure-subblock">
                <h3 className="disclosure-heading">
                  <FiAlertTriangle className="disclosure-icon" /> Law enforcement:
                </h3>
                <p className="privacy-paragraph">
                  Under certain circumstances, the Company may be required to disclose Your Personal Data if required to do so by law or in response to valid requests by public authorities (e.g. a court or a government agency).
                </p>
              </div>

              <div className="disclosure-subblock">
                <h3 className="disclosure-heading">
                  <FiShield className="disclosure-icon" /> Other legal requirements:
                </h3>
                <p className="privacy-paragraph">
                  The Company may disclose Your Personal Data in the good faith belief that such action is necessary to:
                </p>
                <ul className="privacy-styled-bullets">
                  <li>Comply with a legal obligation</li>
                  <li>Protect and defend the rights or property of the Company</li>
                  <li>Prevent or investigate possible wrongdoing in connection with the Service</li>
                  <li>Protect the personal safety of Users of the Service or the public</li>
                  <li>Protect against legal liability</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 9: Security of Your Personal Data */}
          <section id="security-of-data" className="privacy-card">
            <div className="privacy-card-header">
              <div className="privacy-section-icon-box box-blue">
                <FiLock className="privacy-section-icon" />
              </div>
              <h2 className="privacy-card-title">
                9. Security of Your <span className="title-gradient">Personal Data</span>
              </h2>
            </div>

            <div className="privacy-card-body">
              <div className="security-notice-callout">
                <FiShield className="security-callout-icon" />
                <p className="privacy-paragraph" style={{ marginBottom: 0 }}>
                  The security of Your Personal Data is important to Us, but remember that no method of
                  transmission over the Internet, or method of electronic storage is 100% secure. While We
                  strive to use commercially acceptable means to protect Your Personal Data, We cannot
                  guarantee its absolute security.
                </p>
              </div>
            </div>
          </section>

          {/* Section 10: Children's Privacy */}
          <section id="childrens-privacy" className="privacy-card">
            <div className="privacy-card-header">
              <div className="privacy-section-icon-box box-indigo">
                <FiUsers className="privacy-section-icon" />
              </div>
              <h2 className="privacy-card-title">
                10. Children's <span className="title-gradient">Privacy</span>
              </h2>
            </div>

            <div className="privacy-card-body">
              <p className="privacy-paragraph">
                Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If You are a parent or guardian and You are aware that Your child has provided Us with Personal Data, please contact Us. If We become aware that We have collected Personal Data from anyone under the age of 13 without verification of parental consent, We take steps to remove that information from Our servers.
              </p>
              <p className="privacy-paragraph">
                If We need to rely on consent as a legal basis for processing Your information and Your country requires consent from a parent, We may require Your parent's consent before We collect and use that information.
              </p>
            </div>
          </section>

          {/* Section 11: Links to Other Websites */}
          <section id="links-to-other-websites" className="privacy-card">
            <div className="privacy-card-header">
              <div className="privacy-section-icon-box box-cyan">
                <FiExternalLink className="privacy-section-icon" />
              </div>
              <h2 className="privacy-card-title">
                11. Links to Other <span className="title-gradient">Websites</span>
              </h2>
            </div>

            <div className="privacy-card-body">
              <p className="privacy-paragraph">
                Our Service may contain links to other websites that are not operated by Us. If You click on a third party link, You will be directed to that third party's site. We strongly advise You to review the Privacy Policy of every site You visit.
              </p>
              <p className="privacy-paragraph">
                We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.
              </p>
            </div>
          </section>

          {/* Section 12: Changes to this Privacy Policy */}
          <section id="changes-to-policy" className="privacy-card">
            <div className="privacy-card-header">
              <div className="privacy-section-icon-box box-teal">
                <FiRefreshCw className="privacy-section-icon" />
              </div>
              <h2 className="privacy-card-title">
                12. Changes to this <span className="title-gradient">Privacy Policy</span>
              </h2>
            </div>

            <div className="privacy-card-body">
              <p className="privacy-paragraph">
                We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new Privacy Policy on this page.
              </p>
              <p className="privacy-paragraph">
                We will let You know via email and/or a prominent notice on Our Service, prior to the change becoming effective and update the "Last Updated On" date at the top of this Privacy Policy.
              </p>
              <p className="privacy-paragraph">
                You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
              </p>
            </div>
          </section>

          {/* Section 13: Contact Us */}
          <section id="contact-us" className="privacy-card">
            <div className="privacy-card-header">
              <div className="privacy-section-icon-box box-blue">
                <FiMail className="privacy-section-icon" />
              </div>
              <h2 className="privacy-card-title">
                13. Contact <span className="title-gradient">Us</span>
              </h2>
            </div>

            <div className="privacy-card-body">
              <p className="privacy-paragraph">
                If you have any questions about this Privacy Policy, You can contact us:
              </p>
              <div className="privacy-contact-card">
                <div className="contact-card-icon-box">
                  <FiMail className="contact-mail-icon" />
                </div>
                <div className="contact-card-details">
                  <div className="contact-card-label">By visiting this page on our website:</div>
                  <a
                    href="https://praveen-kumar-b.web.app/contact"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-card-button"
                  >
                    <span>Contact If Any Queries</span>
                    <FiChevronRight className="btn-arrow" />
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Section 14: Note & Related Policies */}
          <section id="policy-notes" className="privacy-card">
            <div className="privacy-card-header">
              <div className="privacy-section-icon-box box-purple">
                <FiShield className="privacy-section-icon" />
              </div>
              <h2 className="privacy-card-title">
                14. Note & Related <span className="title-gradient">Policies</span>
              </h2>
            </div>

            <div className="privacy-card-body">
              <p className="privacy-paragraph">
                You Can View Our <strong>Privacy Policy</strong>, <strong>Terms & Conditions</strong>, and <strong>Cookie Policy</strong> Below.
              </p>

              <div className="related-policies-grid">
                <Link to="/privacy-policy" className="policy-link-card active">
                  <span className="policy-arrow">➔</span>
                  <div className="policy-info">
                    <span className="policy-name">View Privacy Policy</span>
                    <span className="policy-sub">Current Document</span>
                  </div>
                  <FiChevronRight className="policy-link-chevron" />
                </Link>

                <a
                  href="https://policiespraveenkumarb.netlify.app/terms-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="policy-link-card"
                >
                  <span className="policy-arrow">➔</span>
                  <div className="policy-info">
                    <span className="policy-name">View Terms & Conditions</span>
                    <span className="policy-sub">Service Usage Guidelines</span>
                  </div>
                  <FiChevronRight className="policy-link-chevron" />
                </a>

                <a
                  href="https://policiespraveenkumarb.netlify.app/cookie-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="policy-link-card"
                >
                  <span className="policy-arrow">➔</span>
                  <div className="policy-info">
                    <span className="policy-name">View Cookie Policy</span>
                    <span className="policy-sub">Tracking & Preferences</span>
                  </div>
                  <FiChevronRight className="policy-link-chevron" />
                </a>
              </div>
            </div>
          </section>
        </main>
      </div>

      {/* Floating Back-To-Top Button */}
      {showBackToTop && (
        <button
          className="privacy-back-to-top"
          onClick={scrollToTop}
          title="Back to Top"
          aria-label="Back to Top"
        >
          <FiArrowUp />
        </button>
      )}
    </div>
  );
};

export default PrivacyPolicy;
